import { Component, useContext } from 'react';
import { Button } from 'reactstrap';
import { GroupPermissions } from 'components/GroupPermissions';
import { ModalConfirm } from 'components/ModalConfirm';
import { DoneInfo, ModalPermissionGroupAddOrUpdate } from 'components/ModalPermissionGroupAddOrUpdate';
import { Loader } from 'components/Loader';
import { Group, PermissionGroup } from 'logic/apiCalls';
import { NotificationContext, NotificationContextState } from 'context/NotificationContext';
import * as apiCalls from 'logic/apiCalls';
import log from 'loglevel';

type TabReplayGroupsInternalProps = {
    notificationContext: NotificationContextState
}

type TabReplayGroupsInternalState = {
    azureGroups: Array<Group>,
    replayPermissionGroups: Array<PermissionGroup>,
    availablePermissions: Array<string>,
    isLoading: boolean,
    showApiError: boolean,
    isDeleteModalOpen: boolean,            
    groupToDelete: Group | null,
    isAddOrUpdateModalOpen: boolean,            
    groupToUpdate: PermissionGroup | null
}

class TabReplayGroupsInternal extends Component<TabReplayGroupsInternalProps, TabReplayGroupsInternalState> {
    state: TabReplayGroupsInternalState = {
        azureGroups: [],
        replayPermissionGroups: [],
        availablePermissions: [],
        isLoading: true,
        showApiError: false,
        isDeleteModalOpen: false,            
        groupToDelete: null,
        isAddOrUpdateModalOpen: false,            
        groupToUpdate: null
    }

    async componentDidMount() {
        await this.loadPageData();
    }

    toggleDeleteModal = () => {
        this.setState({
            isDeleteModalOpen: !this.state.isDeleteModalOpen
        });
    }

    toggleAddOrUpdateModal = () => {
        this.setState({
            isAddOrUpdateModalOpen: !this.state.isAddOrUpdateModalOpen
        });
    }

    openAddModal = () => {
        this.setState({
            isAddOrUpdateModalOpen: true,
            groupToUpdate: null
        });
    }

    openUpdateModal = (permissionGroupToUpdate: PermissionGroup) => {
        this.setState({
            isAddOrUpdateModalOpen: true,
            groupToUpdate: permissionGroupToUpdate
        });
    }

    loadPageData = async () => {
        try {
            const azureGroupsPromise = apiCalls.getAzureGroups();
            const replayPermissionGroupsPromise = apiCalls.getReplayPermissionGroups();
            const availablePermissionsPromise = apiCalls.getAvailableReplayPermissions();

            const [azureGroups, replayPermissionGroups, availablePermissions] = await Promise.all([azureGroupsPromise, replayPermissionGroupsPromise, availablePermissionsPromise]);

            // TODO: Remove this filter when delete is implemented in replay interface. 
            const filteredAvailablePermissions = availablePermissions.filter(permission => !permission.startsWith("Delete")); // Remove all Delete permission.

            this.setState({
                azureGroups: azureGroups,
                replayPermissionGroups: replayPermissionGroups,
                availablePermissions: filteredAvailablePermissions,
                isLoading: false,
                showApiError: false
            });
        }
        catch (error) {
            log.error(`Something went wrong: ${error}`);

            this.setState({
                isLoading: false,
                showApiError: true
            });
        }
    }

    handleEditButtonClicked = (groupToUpdate: PermissionGroup) => {
        this.openUpdateModal(groupToUpdate);
    }

    handleDeleteButtonClicked = (groupToDelete: PermissionGroup) => {
        this.setState({
            groupToDelete: groupToDelete
        });

        this.toggleDeleteModal();
    }

    executeDelete = async () => {
        const permissionGroupToDelete = this.state.groupToDelete;

        if (!permissionGroupToDelete) {
            return;
        }

        log.debug(`[executeDelete] Removing all permissions from the group '${permissionGroupToDelete.name} (${permissionGroupToDelete.id})'.`);

        try {
            // The group is not actually removed, but instead an empty list of permissions is set.
            await apiCalls.setReplayPermissions(permissionGroupToDelete.id, []);
            await this.loadPageData();            
            this.props.notificationContext.setNotification("Replay configuration modified", `Successfully removed permissions from group '${permissionGroupToDelete.name}'.`, 'success');
        }
        catch (error)
        {
            log.error(`Something went wrong: ${error}`);
            this.props.notificationContext.setNotification("Error", `Error during saving of the replay configuration.`, 'danger');
        }
        finally {
            this.toggleDeleteModal();
        }
    }

    executeAddOrUpdate = async (doneInfo: DoneInfo ) => {
        log.debug(`[executeAddOrUpdate] Adding/updating permission group '${doneInfo.group.name} (${doneInfo.group.id})' with permissions '${doneInfo.permissions}'...`);

        try {
            await apiCalls.setReplayPermissions(doneInfo.group.id, doneInfo.permissions);
            await this.loadPageData();

            const notificationText = doneInfo.isUpdate ? `Successfully updated permission of group '${doneInfo.group.name}'.` : `Successfully added permission group '${doneInfo.group.name}'.`;
            this.props.notificationContext.setNotification("Replay configuration modified", notificationText, 'success');
        }
        catch (error)
        {
            log.error(`Something went wrong: ${error}`);
            this.props.notificationContext.setNotification("Error", `Error during saving of the replay configuration.`, 'danger');
        }
        finally {
            this.toggleAddOrUpdateModal();
        }
    }

    render() {
        const groupSettingsRendered = <div className="my-3">
            <GroupPermissions name="replay" permissionGroups={this.state.replayPermissionGroups} handleEdit={this.handleEditButtonClicked} handleDelete={this.handleDeleteButtonClicked}/>
            <Button className='mt-3' color='primary' onClick={this.openAddModal}>Assign</Button>
        </div>

        const groupsWithoutConfig = this.state.azureGroups.filter(azureGroup => !this.state.replayPermissionGroups.some(replayPermissionGroup => azureGroup.id === replayPermissionGroup.id));
        const groupToDeleteName = this.state.groupToDelete?.name || '';

        return (
            <Loader isLoading={this.state.isLoading} showApiError={this.state.showApiError}>
                <ModalConfirm
                    headerText="Confirm delete"
                    detailedText={`Are you sure you want to delete the permissions from group '${groupToDeleteName}'?`}
                    isOpen={this.state.isDeleteModalOpen}
                    onToggle={this.toggleDeleteModal}
                    onConfirm={this.executeDelete} />
                <ModalPermissionGroupAddOrUpdate isOpen={this.state.isAddOrUpdateModalOpen} groups={groupsWithoutConfig} groupToUpdate={this.state.groupToUpdate} availablePermissions={this.state.availablePermissions} onToggle={this.toggleAddOrUpdateModal} onAddOrUpdate={this.executeAddOrUpdate} />
                {groupSettingsRendered}
            </Loader>
        );
    }
}

// Inject the required contexts.
export const TabReplayGroups = () => {
    const notificationContext = useContext(NotificationContext) as NotificationContextState;

    return (
        <TabReplayGroupsInternal notificationContext={notificationContext} />
    )
}